// src/components/operationsAssociate/CreateRFPs.jsx
import { useState, useEffect } from "react";
import { createRFP } from "../../api/OAprocurement-api";
import { getVendors } from "../../api/vendorApi"; // Import getVendors

const CreateRFPForm = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    projectName: "",
    projectDescription: "",
    objectives: "",
    scope: "",
    requirements: "",
    evaluationCriteria: "",
    budgetRange: "",
    issueDate: "",
    submissionDeadline: "",
    proposalValidityPeriod: "",
    contactPerson: "",
    contactEmail: "",
    files: [],
    status: "draft",
    attachments: [
      { name: "Company Profile", required: false },
      { name: "Financial Statements", required: false },
      { name: "Technical Proposal", required: false },
      { name: "Past Experience Documents", required: false }
    ]
  });

  const [loading, setLoading] = useState(false);

  // NEW: Vendor selection states
  const [vendors, setVendors] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [vendorError, setVendorError] = useState(null);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // NEW: Fetch vendors on component mount
  useEffect(() => {
    const fetchVendors = async () => {
      setLoadingVendors(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setVendorError("Authentication token not found. Please log in.");
          return;
        }

        console.log("Fetching vendors for RFP...");
        const response = await getVendors();
        console.log("Vendors response:", response);
        
        let vendorData = [];
        if (Array.isArray(response)) {
          vendorData = response;
        } else if (response?.data) {
          vendorData = Array.isArray(response.data) ? response.data : response.data.data || [];
        }
        
        console.log("Processed vendor data:", vendorData);
        setVendors(Array.isArray(vendorData) ? vendorData : []);
      } catch (error) {
        console.error("Failed to load vendors:", error);
        setVendorError(`Failed to load vendors: ${error.message}`);
        setVendors([]);
      } finally {
        setLoadingVendors(false);
      }
    };

    fetchVendors();
  }, []);

  // NEW: Handle vendor selection
  const handleVendorToggle = (vendor) => {
    const isSelected = selectedVendors.some(v => v._id === vendor._id);
    
    if (isSelected) {
      setSelectedVendors(selectedVendors.filter(v => v._id !== vendor._id));
    } else {
      setSelectedVendors([...selectedVendors, vendor]);
    }
  };

  // NEW: Select all vendors
  const handleSelectAllVendors = () => {
    if (selectedVendors.length === vendors.length) {
      setSelectedVendors([]);
    } else {
      setSelectedVendors([...vendors]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, files: e.target.files });
  };

  const handleAttachmentChange = (index, field, value) => {
    const updatedAttachments = [...formData.attachments];
    updatedAttachments[index][field] = value;
    setFormData({ ...formData, attachments: updatedAttachments });
  };

  // NEW: Form validation
  const validateForm = () => {
    const errors = {};
    if (!formData.projectName.trim()) errors.projectName = "Project name is required.";
    if (!formData.projectDescription.trim()) errors.projectDescription = "Project description is required.";
    if (!formData.objectives.trim()) errors.objectives = "Objectives are required.";
    if (!formData.scope.trim()) errors.scope = "Scope of work is required.";
    if (!formData.requirements.trim()) errors.requirements = "Requirements are required.";
    if (!formData.evaluationCriteria.trim()) errors.evaluationCriteria = "Evaluation criteria is required.";
    if (!formData.issueDate) errors.issueDate = "Issue date is required.";
    if (!formData.submissionDeadline) errors.submissionDeadline = "Submission deadline is required.";
    if (!formData.contactPerson.trim()) errors.contactPerson = "Contact person is required.";
    if (!formData.contactEmail.trim()) errors.contactEmail = "Contact email is required.";
    
    if (formData.issueDate && formData.submissionDeadline && formData.submissionDeadline <= formData.issueDate) {
      errors.submissionDeadline = "Submission deadline must be after issue date.";
    }
    
    if (selectedVendors.length === 0) {
      errors.vendors = "Please select at least one vendor to notify.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // UPDATED: Include selected vendors in RFP data
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);

    try {
      const payload = {
        project: formData.projectName,
        projectDescription: formData.projectDescription,
        objective: formData.objectives,
        scopeOfWork: formData.scope,
        proposalRequirement: formData.requirements,
        evaluationCriteria: formData.evaluationCriteria,
        budgetRange: formData.budgetRange,
        issueDate: formData.issueDate,
        submissionDeadline: formData.submissionDeadline,
        proposalValidityPeriod: formData.proposalValidityPeriod,
        contactPerson: formData.contactPerson,
        contactEmail: formData.contactEmail,
        status: formData.status,
        attachments: formData.attachments,
        files: formData.files,
        // NEW: Include selected vendors
        notifiedVendors: selectedVendors.map(vendor => ({
          vendorId: vendor._id,
          vendorName: vendor.name,
          vendorCode: vendor.vendorCode || vendor.code || '',
          email: vendor.email || vendor.contactEmail,
          contactPerson: vendor.contactPerson || vendor.contactName
        })),
        vendorEmails: selectedVendors.map(vendor => vendor.email || vendor.contactEmail).filter(Boolean)
      };

      await createRFP(payload);

      console.log("RFP created successfully!");
      alert(`RFP created successfully and sent to ${selectedVendors.length} vendor(s)!`);
      
      if (onSuccess) {
        await onSuccess();
      }
    } catch (error) {
      console.error("Error creating RFP:", error);
      alert("Failed to create RFP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Create New RFP (Request for Proposal)</h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Project Information Section */}
        <div className="border-b pb-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">Project Information</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Name *
              </label>
              <input
                type="text"
                name="projectName"
                value={formData.projectName}
                onChange={handleChange}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${formErrors.projectName ? "border-red-500" : "border-gray-300"}`}
                placeholder="e.g., IT Infrastructure Upgrade Project"
                required
              />
              {formErrors.projectName && <p className="text-red-500 text-sm mt-1">{formErrors.projectName}</p>}
            </div>

            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Description *
              </label>
              <textarea
                name="projectDescription"
                value={formData.projectDescription}
                onChange={handleChange}
                rows={4}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${formErrors.projectDescription ? "border-red-500" : "border-gray-300"}`}
                placeholder="Provide a comprehensive overview of the project..."
                required
              />
              {formErrors.projectDescription && <p className="text-red-500 text-sm mt-1">{formErrors.projectDescription}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Objectives *
              </label>
              <textarea
                name="objectives"
                value={formData.objectives}
                onChange={handleChange}
                rows={3}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${formErrors.objectives ? "border-red-500" : "border-gray-300"}`}
                placeholder="List the main goals and objectives..."
                required
              />
              {formErrors.objectives && <p className="text-red-500 text-sm mt-1">{formErrors.objectives}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scope of Work *
              </label>
              <textarea
                name="scope"
                value={formData.scope}
                onChange={handleChange}
                rows={3}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${formErrors.scope ? "border-red-500" : "border-gray-300"}`}
                placeholder="Define what is included and excluded..."
                required
              />
              {formErrors.scope && <p className="text-red-500 text-sm mt-1">{formErrors.scope}</p>}
            </div>

            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Requirements & Specifications *
              </label>
              <textarea
                name="requirements"
                value={formData.requirements}
                onChange={handleChange}
                rows={4}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${formErrors.requirements ? "border-red-500" : "border-gray-300"}`}
                placeholder="Technical requirements, compliance standards, performance criteria..."
                required
              />
              {formErrors.requirements && <p className="text-red-500 text-sm mt-1">{formErrors.requirements}</p>}
            </div>
          </div>
        </div>

        {/* NEW: Vendor Selection Section */}
        <div className="border-b pb-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">Vendor Notification</h3>
          
          {vendorError && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-3">
              {vendorError}
            </div>
          )}

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Vendors to Notify *
            </label>
            <button
              type="button"
              onClick={() => setShowVendorDropdown(!showVendorDropdown)}
              className={`w-full border rounded-lg px-3 py-2 text-left bg-white flex justify-between items-center ${formErrors.vendors ? "border-red-500" : "border-gray-300"} ${loadingVendors ? 'cursor-wait' : 'cursor-pointer'}`}
              disabled={loadingVendors}
            >
              <span className="text-gray-700">
                {loadingVendors ? "Loading vendors..." : 
                 selectedVendors.length === 0 ? "Select vendors to notify..." :
                 `${selectedVendors.length} vendor(s) selected`}
              </span>
              <svg className={`w-4 h-4 transition-transform ${showVendorDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showVendorDropdown && !loadingVendors && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div className="p-2 border-b border-gray-200">
                  <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={selectedVendors.length === vendors.length && vendors.length > 0}
                      onChange={handleSelectAllVendors}
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="font-medium text-gray-700">
                      Select All ({vendors.length} vendors)
                    </span>
                  </label>
                </div>

                {vendors.length === 0 ? (
                  <div className="p-4 text-gray-500 text-center">No vendors available</div>
                ) : (
                  vendors.map((vendor) => (
                    <label key={vendor._id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedVendors.some(v => v._id === vendor._id)}
                        onChange={() => handleVendorToggle(vendor)}
                        className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{vendor.name}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {vendor.vendorCode || vendor.code ? `Code: ${vendor.vendorCode || vendor.code} | ` : ''}
                          {vendor.email || vendor.contactEmail || 'No email'}
                        </div>
                      </div>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>

          {formErrors.vendors && <p className="text-red-500 text-sm mt-1">{formErrors.vendors}</p>}

          {selectedVendors.length > 0 && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="text-sm font-medium text-green-800 mb-2">
                Selected Vendors ({selectedVendors.length}):
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedVendors.map((vendor) => (
                  <span key={vendor._id} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {vendor.name}
                    <button
                      type="button"
                      onClick={() => handleVendorToggle(vendor)}
                      className="ml-1 text-green-600 hover:text-green-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Evaluation & Budget Section */}
        <div className="border-b pb-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">Evaluation & Budget</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Evaluation Criteria *
              </label>
              <textarea
                name="evaluationCriteria"
                value={formData.evaluationCriteria}
                onChange={handleChange}
                rows={3}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${formErrors.evaluationCriteria ? "border-red-500" : "border-gray-300"}`}
                placeholder="How proposals will be evaluated (e.g., technical approach 40%, cost 30%, experience 30%)"
                required
              />
              {formErrors.evaluationCriteria && <p className="text-red-500 text-sm mt-1">{formErrors.evaluationCriteria}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Budget Range
              </label>
              <input
                type="text"
                name="budgetRange"
                value={formData.budgetRange}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="e.g., $50,000 - $100,000 (optional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Proposal Validity Period
              </label>
              <input
                type="text"
                name="proposalValidityPeriod"
                value={formData.proposalValidityPeriod}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="e.g., 90 days from submission"
              />
            </div>
          </div>
        </div>

        {/* Timeline & Contact Section */}
        <div className="border-b pb-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">Timeline & Contact Information</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Issue Date *
              </label>
              <input
                type="date"
                name="issueDate"
                value={formData.issueDate}
                onChange={handleChange}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${formErrors.issueDate ? "border-red-500" : "border-gray-300"}`}
                required
              />
              {formErrors.issueDate && <p className="text-red-500 text-sm mt-1">{formErrors.issueDate}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Submission Deadline *
              </label>
              <input
                type="date"
                name="submissionDeadline"
                value={formData.submissionDeadline}
                onChange={handleChange}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${formErrors.submissionDeadline ? "border-red-500" : "border-gray-300"}`}
                required
              />
              {formErrors.submissionDeadline && <p className="text-red-500 text-sm mt-1">{formErrors.submissionDeadline}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Person *
              </label>
              <input
                type="text"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleChange}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${formErrors.contactPerson ? "border-red-500" : "border-gray-300"}`}
                placeholder="Project Manager Name"
                required
              />
              {formErrors.contactPerson && <p className="text-red-500 text-sm mt-1">{formErrors.contactPerson}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Email *
              </label>
              <input
                type="email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleChange}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${formErrors.contactEmail ? "border-red-500" : "border-gray-300"}`}
                placeholder="contact@company.com"
                required
              />
              {formErrors.contactEmail && <p className="text-red-500 text-sm mt-1">{formErrors.contactEmail}</p>}
            </div>
          </div>
        </div>

        {/* Additional Information Section */}
        <div>
          <h3 className="text-lg font-semibold mb-4 text-gray-700">Additional Information</h3>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Required Attachments from Vendors
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {formData.attachments.map((attachment, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={attachment.required}
                        onChange={(e) => handleAttachmentChange(index, 'required', e.target.checked)}
                        className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="text-sm font-medium">{attachment.name}</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {attachment.required ? 'Required' : 'Optional'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attach Supporting Documents
                </label>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {loading ? "Creating..." : "Create RFP"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateRFPForm;
